import React, { useEffect, useState, useRef } from 'react';
import { useDrawers } from '../../contexts/DrawerContext';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { UploadCloud, File, X, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';
import { cn } from '../../lib/utils';
type UploadState = 'empty' | 'dragging' | 'selected' | 'uploading' | 'success' | 'error';
const inputClass = 'w-full h-12 px-4 rounded-xl bg-surface border border-border text-ink focus:border-accent focus:ring-2 focus:ring-accent/25 outline-none transition';
const labelClass = 'block text-sm font-medium text-ink mb-1.5';
export function UploadDrawer() {
  const {
    isUploadOpen,
    closeUpload
  } = useDrawers();
  const [uploadState, setUploadState] = useState<UploadState>('empty');
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!isUploadOpen) {
      setTimeout(() => {
        setUploadState('empty');
        setProgress(0);
      }, 300);
    }
  }, [isUploadOpen]);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (uploadState === 'empty' || uploadState === 'error') {
      setUploadState('dragging');
    }
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (uploadState === 'dragging') {
      setUploadState('empty');
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setUploadState('selected');
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadState('selected');
    }
  };
  const simulateUpload = (e: React.FormEvent) => {
    e.preventDefault();
    if (uploadState !== 'selected') return;
    setUploadState('uploading');
    let p = 0;
    const interval = setInterval(() => {
      p += 10;
      setProgress(p);
      if (p >= 100) {
        clearInterval(interval);
        setUploadState('success');
      }
    }, 200);
  };
  return <Drawer isOpen={isUploadOpen} onClose={closeUpload} title="Upload a contract" subtitle={uploadState === 'success' ? undefined : 'Reviewed by a solicitor, not a checklist'} data-id="element-131" data-mp="1rx8rlx4rs55q">
      <div className="p-6" data-id="element-132" data-mp="21udddvupdym0">
        {uploadState === 'success' ? <div className="py-12 flex flex-col items-center text-center space-y-6 animate-in zoom-in-95 duration-300" data-id="element-133" data-mp="14a0pfo2n1c7x">
            <div className="w-16 h-16 bg-accent-soft rounded-full flex items-center justify-center text-accent" data-id="element-134" data-mp="19827q6s8p7ic">
              <CheckCircle2 className="w-8 h-8" data-id="element-135" data-mp="1e52bgb1m9y6a" />
            </div>
            <div className="space-y-2" data-id="element-136" data-mp="1iz6isveso87w">
              <h3 className="text-2xl font-serif text-ink" data-id="element-137" data-mp="li7u3n03jex1">
                Document received.
              </h3>
              <p className="text-muted-ink leading-relaxed max-w-xs mx-auto" data-id="element-138" data-mp="qh14v7o00rti">
                The team will review the document and contact you about the
                proposed next step.
              </p>
            </div>
            <Button onClick={closeUpload} variant="outline" className="mt-2" data-id="element-139" data-mp="dzqhh7wxbjzu">
              Close
            </Button>
          </div> : <form onSubmit={simulateUpload} className="space-y-6" data-id="element-140" data-mp="91f5orn2izez">
            <div className="flex items-start gap-3 rounded-xl bg-accent-soft border border-accent-light p-4" data-id="element-141" data-mp="3zzk8ygzaqw2">
              <ShieldCheck className="w-5 h-5 text-accent shrink-0 mt-0.5" data-id="element-142" data-mp="8la7hat4gg" />
              <p className="text-sm text-ink leading-relaxed" data-id="element-143" data-mp="3rcticnzsegxv">
                Send your contract and any disclosure documents and the team
                will read them before you are committed.
              </p>
            </div>

            {/* Drag Drop Zone */}
            <div className={cn('rounded-2xl border-2 border-dashed p-8 flex flex-col items-center justify-center text-center transition-colors relative', uploadState === 'dragging' ? 'border-accent bg-accent-soft' : 'border-border bg-surface', uploadState === 'error' && 'border-red-300 bg-red-50', (uploadState === 'selected' || uploadState === 'uploading') && 'border-solid border-border bg-soft-panel')} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => uploadState === 'empty' && fileInputRef.current?.click()} data-id="element-144" data-mp="3cg2pyyoj65hr">
              <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.doc,.docx" onChange={handleFileSelect} data-id="element-145" data-mp="35epj7bjk2ful" />

              {uploadState === 'empty' || uploadState === 'dragging' ? <>
                  <div className="w-12 h-12 rounded-2xl bg-accent-soft flex items-center justify-center mb-4" data-id="element-146" data-mp="17negcwr8j3ec">
                    <UploadCloud className={cn('w-6 h-6', uploadState === 'dragging' ? 'text-accent' : 'text-accent/80')} data-id="element-147" data-mp="2s701qkqj1t51" />
                  </div>
                  <p className="text-ink font-semibold mb-1" data-id="element-148" data-mp="2x48n0u6z2dai">
                    Click or drag a file to upload
                  </p>
                  <p className="text-xs text-muted-ink" data-id="element-149" data-mp="36vmrlgds63ai">
                    Accepted files: PDF, DOC, DOCX
                  </p>
                </> : uploadState === 'error' ? <>
                  <AlertCircle className="w-10 h-10 mb-4 text-red-500" data-id="element-150" data-mp="3fvjcr80yok0t" />
                  <p className="text-ink font-semibold mb-1" data-id="element-151" data-mp="1qh4p6r1oj36w">Upload failed</p>
                  <button type="button" onClick={() => setUploadState('empty')} className="text-sm text-accent font-semibold hover:underline" data-id="element-152" data-mp="1qj7d95j9um8z">
                    Try again
                  </button>
                </> : <div className="w-full flex items-center gap-4 text-left" data-id="element-153" data-mp="3ql75ttz89pxu">
                  <div className="bg-surface p-3 rounded-xl border border-border shrink-0" data-id="element-154" data-mp="3vh5s9c0zwxvm">
                    <File className="w-6 h-6 text-accent" data-id="element-155" data-mp="3g7rnvlpiwb0" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-156" data-mp="8cy8wm98zxy0">
                    <p className="text-sm font-semibold text-ink truncate" data-id="element-157" data-mp="dcue2otolvf5">
                      contract_of_sale.pdf
                    </p>
                    <p className="text-xs text-muted-ink" data-id="element-158" data-mp="i4vmu0rn540f">
                      {uploadState === 'uploading' ? `Uploading ${progress}%` : '2.4 MB'}
                    </p>
                  </div>
                  {uploadState === 'selected' && <button type="button" onClick={e => {
              e.stopPropagation();
              setUploadState('empty');
            }} className="p-2 rounded-full text-muted-ink hover:text-ink hover:bg-surface" aria-label="Remove file" data-id="element-159" data-mp="w4ddklohyrcf">
                      <X className="w-4 h-4" data-id="element-160" data-mp="22md3x5p4gsqb" />
                    </button>}
                </div>}

              {uploadState === 'uploading' && <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-border rounded-b-2xl overflow-hidden" data-id="element-161" data-mp="2v9qvspdyqy3k">
                  <div className="h-full bg-accent transition-all duration-200 ease-out" style={{
              width: `${progress}%`
            }} data-id="element-162" data-mp="2qiqyhj0xme0j" />
                </div>}
            </div>

            <div className="space-y-4 pt-2" data-id="element-163" data-mp="3e9el7afib875">
              <div className="grid grid-cols-2 gap-4" data-id="element-164" data-mp="3e7k0nx85di3l">
                <div data-id="element-165" data-mp="2ml1synilodp8">
                  <label className={labelClass} data-id="element-166" data-mp="2mkikryir53oe">Full name</label>
                  <input required type="text" autoComplete="name" className={inputClass} data-id="element-167" data-mp="2rc0b7sn5flv4" />
                </div>
                <div data-id="element-168" data-mp="2hlxkjz3i2jzy">
                  <label className={labelClass} data-id="element-169" data-mp="2hnidxle2vww8">Phone</label>
                  <input required type="tel" autoComplete="tel" className={inputClass} data-id="element-170" data-mp="35c1m80x4o9ob" />
                </div>
              </div>

              <div data-id="element-171" data-mp="2wc4svlzsmmmx">
                <label className={labelClass} data-id="element-172" data-mp="2walfq49forpd">Email</label>
                <input required type="email" autoComplete="email" className={inputClass} data-id="element-173" data-mp="30cot4yfny6da" />
              </div>

              <div data-id="element-174" data-mp="1oxjin89aj3j4">
                <label className={labelClass} data-id="element-175" data-mp="1oyup5fu4epdx">Property address</label>
                <input required type="text" className={inputClass} data-id="element-176" data-mp="1he0ktvve15qh" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-177" data-mp="1cf7i8yhn2z53">
                <div data-id="element-178" data-mp="1ceeabu6y23ld">
                  <label className={labelClass} data-id="element-179" data-mp="1cha6bcz8ei76">Matter type</label>
                  <select className={`${inputClass} appearance-none`} data-id="element-180" data-mp="1rb59c2cphdag">
                    <option data-id="element-181" data-mp="1mcu0nuy5d8tt">Buying</option>
                    <option data-id="element-182" data-mp="1mdwotv434x61">Selling</option>
                    <option data-id="element-183" data-mp="ynj64v7e4ao4">Contract review</option>
                  </select>
                </div>
                <div data-id="element-184" data-mp="toy4qsgejky3">
                  <label className={labelClass} data-id="element-185" data-mp="tphehlvwf311">Deadline</label>
                  <input type="date" className={inputClass} data-id="element-186" data-mp="18dlwsor1hcja" />
                </div>
              </div>

              <div data-id="element-187" data-mp="13mvujkdwtpst">
                <label className={labelClass} data-id="element-188" data-mp="13mc93tp1wja1">Message</label>
                <textarea rows={2} className="w-full p-4 rounded-xl bg-surface border border-border text-ink focus:border-accent focus:ring-2 focus:ring-accent/25 outline-none transition resize-none" data-id="element-189" data-mp="13ksvybyoyoch"></textarea>
              </div>

              <Button fullWidth variant="accent" type="submit" disabled={uploadState !== 'selected'} className="mt-2" data-id="element-190" data-mp="3kscfmrasgywu">
                {uploadState === 'uploading' ? 'Uploading...' : 'Submit document'}
              </Button>

              {/* CODE COMMENT: This wording needs legal approval before production. */}
              <p className="text-xs text-muted-ink text-center" data-id="element-191" data-mp="2x31cj35yusfj">
                Submitting information does not by itself create a
                solicitor-client relationship. Engagement is confirmed
                separately.
              </p>
            </div>
          </form>}
      </div>
    </Drawer>;
}